import { fg } from '@atlassian/jira-feature-gating';
import { getDefaultOptions } from '@atlassian/jira-fetch/src/utils/fetch-default-options.tsx';
import {
	performGetRequest as jiraPerformGetRequest,
	performPostRequest as jiraPerformPostRequest,
	performPutRequest as jiraPerformPutRequest,
	performDeleteRequest as jiraPerformDeleteRequest,
	performPatchRequest as jiraPerformPatchRequest,
} from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fireCmdbErrorAnalytics } from '@jira/servicedesk__insight-common-utils-cmdb-error-handling/src/fireCmdbErrorAnalytics.tsx';
import { getRequestTracking } from './getRequestTracking.tsx';
import { getErrorStatusCode, isRetryableCmdbError } from './isRetryableCmdbError.tsx';

type RetryPredicate = (error: unknown) => boolean;

// Single retry with a fixed 1s delay for transient CMDB read failures.
const RETRY_DELAY_MS = 1000;

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const fireRetryAnalytics = (
	phase: 'retryTriggered' | 'retrySucceeded' | 'retryFailed',
	url: string,
	method: 'GET' | 'POST',
	error?: unknown,
): void => {
	const { requestUrl, requestUrlTemplate } = getRequestTracking(url);
	fireCmdbErrorAnalytics({
		meta: {
			id: 'cmdbRequestRetry',
			packageName: 'jiraServicedeskInsightCommonUtilsCmdbClient',
			teamName: 'astral',
		},
		...(error instanceof Error ? { error } : {}),
		attributes: {
			phase,
			method,
			requestUrl,
			requestUrlTemplate,
			statusCode: getErrorStatusCode(error) ?? 'network',
		},
	});
};

// 1 initial attempt + 1 retry.
const MAX_RETRIES = 1;

const withCmdbRetry = async <ResponseType,>(
	url: string,
	method: 'GET' | 'POST',
	retryPredicate: RetryPredicate,
	request: () => Promise<ResponseType>,
	attempt = 0,
): Promise<ResponseType> => {
	try {
		const result = await request();
		if (attempt > 0) {
			fireRetryAnalytics('retrySucceeded', url, method);
		}
		return result;
	} catch (error) {
		if (attempt > 0) {
			fireRetryAnalytics('retryFailed', url, method, error);
		}
		if (attempt >= MAX_RETRIES || !retryPredicate(error)) {
			throw error;
		}
		fireRetryAnalytics('retryTriggered', url, method, error);
		await delay(RETRY_DELAY_MS);
		return withCmdbRetry(url, method, retryPredicate, request, attempt + 1);
	}
};

export const cmdbRequestSourceHeader: Record<string, string> = {
	'X-Request-Source': 'cmdb-frontend',
};

export const performCmdbGetRequest: typeof jiraPerformGetRequest = (url, options) =>
	jiraPerformGetRequest(url, mergeOptions(url, options));

export const performCmdbPostRequest: typeof jiraPerformPostRequest = (url, options) =>
	jiraPerformPostRequest(url, mergeOptions(url, options));

export const performCmdbGetRequestWithRetry = async <ResponseType = unknown,>(
	url: Parameters<typeof jiraPerformGetRequest>[0],
	options?: Parameters<typeof jiraPerformGetRequest>[1],
	retryPredicate: RetryPredicate = isRetryableCmdbError,
): Promise<ResponseType> => {
	if (!fg('assets_cmdb_object_schema_load_retry')) {
		return performCmdbGetRequest<ResponseType>(url, options);
	}
	return withCmdbRetry(url, 'GET', retryPredicate, () =>
		performCmdbGetRequest<ResponseType>(url, options),
	);
};

export const performCmdbPostRequestWithRetry = async <ResponseType = unknown,>(
	url: Parameters<typeof jiraPerformPostRequest>[0],
	options?: Parameters<typeof jiraPerformPostRequest>[1],
	retryPredicate: RetryPredicate = isRetryableCmdbError,
): Promise<ResponseType> => {
	if (!fg('assets_cmdb_object_schema_load_retry')) {
		return performCmdbPostRequest<ResponseType>(url, options);
	}
	return withCmdbRetry(url, 'POST', retryPredicate, () =>
		performCmdbPostRequest<ResponseType>(url, options),
	);
};

export const performCmdbPutRequest: typeof jiraPerformPutRequest = (url, options) =>
	jiraPerformPutRequest(url, mergeOptions(url, options));

export const performCmdbDeleteRequest: typeof jiraPerformDeleteRequest = (url, options) =>
	jiraPerformDeleteRequest(url, mergeOptions(url, options));

export const performCmdbPatchRequest: typeof jiraPerformPatchRequest = (url, options) =>
	jiraPerformPatchRequest(url, mergeOptions(url, options));

export const mergeOptions = (
	url: string,
	options: RequestInit | undefined,
):
	| RequestInit
	| {
			headers: {};
			body?: BodyInit | null;
			cache?: RequestCache;
			credentials?: RequestCredentials;
			integrity?: string;
			keepalive?: boolean;
			method?: string;
			mode?: RequestMode;
			priority?: RequestPriority;
			redirect?: RequestRedirect;
			referrer?: string;
			referrerPolicy?: ReferrerPolicy;
			signal?: AbortSignal | null;
			window?: null;
	  }
	| undefined => {
	const defaultOptions = getDefaultOptions(url);

	return fg('fly-2529-cmdb-client-send-request-source-header')
		? {
				...defaultOptions,
				...options,
				headers: {
					...defaultOptions.headers,
					...options?.headers,
					...cmdbRequestSourceHeader,
				},
			}
		: options;
};
